import { useParams } from "react-router-dom";
import { useCallback, useEffect } from "react";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useSelectedProjectInfo,
  useSetSelectedOriginalInfo,
} from "../hooks/useTabStore";
import { Skeleton } from "@/components/ui/skeleton";
import { ProjectType } from "@/consts/Project";
import { useFilteredOriginals } from "../hooks/useFilteredOriginals";

export default function EstimateSelect() {
  const { group_id = "" } = useParams();

  const selectedProjectInfo = useSelectedProjectInfo();
  const setSelectedOriginalInfo = useSetSelectedOriginalInfo();

  const { allOriginals, isError, isLoading } = useFilteredOriginals(group_id);

  const handleSelectChange = useCallback(
    (value: string) => {
      const selectedItem = allOriginals.find(
        (item) => item.project_id === value
      );
      if (!selectedItem) return;

      setSelectedOriginalInfo({
        projectId: selectedItem.project_id,
        projectType: selectedItem.project_type,
      });
    },
    [allOriginals, setSelectedOriginalInfo]
  );

  useEffect(() => {
    if (!selectedProjectInfo && allOriginals.length > 0) {
      // 우선순위: 1.입찰공고(PROJ) > 2.사전규격(PRCR) > 3.발주계획(PLAN)
      const priorityOrder = [
        ProjectType.PROJ,
        ProjectType.PRCR,
        ProjectType.PLAN,
      ];

      const sortedByPriority = allOriginals.sort((a, b) => {
        const aIndex = priorityOrder.indexOf(a.project_type);
        const bIndex = priorityOrder.indexOf(b.project_type);
        return aIndex - bIndex;
      });

      const first = sortedByPriority[0];
      setSelectedOriginalInfo({
        projectId: first.project_id,
        projectType: first.project_type,
      });
    }
  }, [selectedProjectInfo, allOriginals, setSelectedOriginalInfo]);

  if (isLoading) {
    return (
      <div className="absolute right-0 -top-14 inline-flex items-center gap-3 justify-end">
        <Skeleton className="w-80 h-10 rounded-sm bg-white" />
      </div>
    );
  }

  if (isError) {
    return null;
  }

  return (
    <div className="absolute right-0 -top-14 inline-flex items-center gap-3 justify-end">
      <p className="text-xs text-gray-B font-bold whitespace-nowrap">공고</p>
      <Select
        onValueChange={handleSelectChange}
        value={selectedProjectInfo?.projectId ?? undefined}
      >
        <SelectTrigger className="min-w-[300px] text-[10px] text-gray-A font-semibold">
          <SelectValue placeholder="공고를 선택하세요" />
        </SelectTrigger>
        <SelectContent className="min-w-[300px]">
          <SelectGroup>
            {allOriginals
              .slice()
              .sort((a, b) => {
                const priorityOrder = [
                  ProjectType.PROJ,
                  ProjectType.PRCR,
                  ProjectType.PLAN,
                ];
                const aIndex = priorityOrder.indexOf(a.project_type);
                const bIndex = priorityOrder.indexOf(b.project_type);
                return aIndex - bIndex;
              })
              .map((item) => (
                <SelectItem
                  key={item.project_id}
                  value={item.project_id}
                  className="!block w-full"
                >
                  <div className="flex justify-between items-center gap-5">
                    <div className="flex items-center gap-2 flex-1">
                      <span className="text-[10px] text-gray-A font-semibold">
                        {item.project_title}
                      </span>
                      <div className="flex flex-shrink-0 whitespace-nowrap">
                        {getBadgeByProjectType(item.project_type)}
                      </div>
                    </div>
                    {item.is_outdated && (
                      <div className="flex py-0.5 px-1 whitespace-nowrap flex-shrink-0 text-[6px] leading-[8px] bg-[rgba(238,142,148,0.30)] text-pink font-semibold rounded-[2px]">
                        연결되지 않음
                      </div>
                    )}
                  </div>
                </SelectItem>
              ))}
          </SelectGroup>
        </SelectContent>
      </Select>
    </div>
  );
}

const getBadgeByProjectType = (type: ProjectType) => {
  const projectTypeMap = {
    [ProjectType.PROJ]: { label: "입찰공고", color: "bg-mediumGreen" },
    [ProjectType.PRCR]: { label: "사전규격", color: "bg-dark-blue" },
    [ProjectType.PLAN]: { label: "발주계획", color: "bg-mandalay" },
  };

  return (
    <div
      className={`flex py-0.5 px-1 rounded-[2px] text-[8px] leading-[10px] text-white font-semibold ${projectTypeMap[type].color}`}
    >
      {projectTypeMap[type].label}
    </div>
  );
};
